<template>
  <div class="sabbatical-adjustment">
    <SabbaticalAdjustmentForm v-if="canStore" @saved="fetchData(1)" />
    <CCard>
      <CCardBody>
        <h5 class="mb-3">{{ $t("sabbatical_adjustment.history_title") }}</h5>
        <CRow>
          <CCol sm="4" class="form-group">
            <label>{{ $t("employee.label_employee_request_search_code_name") }}</label>
            <CInput v-model="filters.employee" @keyup.enter="fetchData(1)" />
          </CCol>
          <CCol sm="2" class="form-group">
            <label>{{ $t("sabbatical_adjustment.from_month") }}</label>
            <b-form-input v-model="filters.from_month" type="month" />
          </CCol>
          <CCol sm="2" class="form-group">
            <label>{{ $t("sabbatical_adjustment.to_month") }}</label>
            <b-form-input v-model="filters.to_month" type="month" />
          </CCol>
          <CCol sm="4" class="btn-group-search">
            <CButton color="primary" @click="fetchData(1)">{{ $t("button.label_employee_btn_search") }}</CButton>
            <CButton color="secondary" class="ml-2" @click="resetFilters">{{ $t("button.label_action_reset") }}</CButton>
          </CCol>
        </CRow>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ $t("sabbatical_adjustment.created_at") }}</th>
                <th>{{ $t("employee.label_employee_code") }}</th>
                <th>{{ $t("employee.label_employee_name") }}</th>
                <th>{{ $t("sabbatical_adjustment.bucket_month") }}</th>
                <th class="text-center">{{ $t("sabbatical_adjustment.days") }}</th>
                <th>{{ $t("sabbatical_adjustment.reason") }}</th>
                <th>{{ $t("sabbatical_adjustment.created_by") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in logs.data" :key="item.id">
                <td>{{ (logs.current_page - 1) * logs.per_page + index + 1 }}</td>
                <td>{{ formatDateTime(item.created_at) }}</td>
                <td>{{ item.employee_id }}</td>
                <td>{{ item.employee_name }}</td>
                <td>{{ formatMonth(item.month) }}</td>
                <td class="text-center" :class="item.number_days_off >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatDays(item.number_days_off) }}
                </td>
                <td class="reason">{{ item.reason }}</td>
                <td>{{ item.created_by_name }}</td>
              </tr>
              <tr v-if="!logs.data.length">
                <td colspan="8" class="text-center">{{ $t("table.label_data_not_found") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <CPagination
          v-if="logs.last_page > 1"
          :activePage="logs.current_page"
          :pages="logs.last_page"
          @update:activePage="fetchData"
          size="sm"
          align="center"
        />
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import api from "../../api/api";
import SabbaticalAdjustmentForm from "./SabbaticalAdjustmentForm.vue";

const STORE_PERMISSION = "sabbatical-adjustments.store";
const PER_PAGE = 20;

const emptyFilters = () => ({ employee: "", from_month: "", to_month: "" });

// Manual sabbatical adjustments: form (add / subtract) + history of manual entries
export default {
  name: "SabbaticalAdjustmentList",
  components: { SabbaticalAdjustmentForm },
  metaInfo() {
    return {
      title: this.$t("sabbatical_adjustment.title"),
      titleTemplate: "%s | " + this.$t("page_title.head_title")
    };
  },
  data() {
    return {
      filters: emptyFilters(),
      logs: { data: [], current_page: 1, last_page: 1, per_page: PER_PAGE }
    };
  },
  computed: {
    ...mapGetters({ account: "auth/account" }),
    canStore() {
      return ((this.account && this.account.permissions) || []).includes(STORE_PERMISSION);
    }
  },
  created() {
    this.fetchData(1);
  },
  methods: {
    ...mapActions("layout", ["setLoading"]),
    formatMonth(month) {
      return month ? moment(month, "YYYY-MM").format("MM/YYYY") : "";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
    },
    formatDays(days) {
      const value = Math.round(parseFloat(days) * 100) / 100;
      return value > 0 ? `+${value}` : `${value}`;
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.fetchData(1);
    },
    async fetchData(page) {
      this.setLoading(true);
      try {
        const response = await axios.get(api.SABBATICAL_ADJUSTMENTS, {
          params: { ...this.filters, page, per_page: PER_PAGE }
        });
        this.logs = response.data;
      } catch (e) {
        // Error toast already shown by axios interceptor
      } finally {
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
label {
  font-weight: 600;
}
.btn-group-search {
  display: flex;
  align-items: flex-end;
  padding-bottom: 1rem;
}
.reason {
  white-space: pre-line;
  word-break: break-word;
}
</style>
